import React, { useEffect, useState } from 'react';
import {
  AdminButton,
  AdminCard,
  AdminInput,
  AdminPageShell,
  AdminSelect,
  AdminStatusPill,
  AdminTable
} from '../AdminUI';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const emptyForm = {
  code: '',
  discount_percent: 10,
  is_active: true,
  max_uses: '',
  starts_at: '',
  expires_at: '',
  notes: ''
};

const PromoCodesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('adminToken');

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load promo codes');
      setItems(data.promoCodes || []);
    } catch (err) {
      setError(err.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const createPromo = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        starts_at: form.starts_at || null,
        expires_at: form.expires_at || null
      };
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create promo code');
      setItems((prev) => [data.promoCode, ...prev]);
      setForm(emptyForm);
    } catch (err) {
      setError(err.message || 'Failed to create promo code');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes/${item.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !item.is_active })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update promo code');
      setItems((prev) => prev.map((p) => (p.id === item.id ? data.promoCode : p)));
    } catch (err) {
      setError(err.message || 'Failed to update promo code');
    }
  };

  const deletePromo = async (item) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete promo code');
      setItems((prev) => prev.filter((p) => p.id !== item.id));
    } catch (err) {
      setError(err.message || 'Failed to delete promo code');
    }
  };

  return (
    <AdminPageShell
      title="Promo Codes"
      description="Create and manage subscription discount codes."
    >
      {error ? <AdminCard className="border-rose-200 text-rose-600 dark:border-rose-900/40">{error}</AdminCard> : null}

      <AdminCard>
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Create Promo Code</h2>
        <form onSubmit={createPromo} className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <AdminInput
            placeholder="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            required
          />
          <AdminInput
            type="number"
            min={1}
            max={100}
            placeholder="Discount %"
            value={form.discount_percent}
            onChange={(e) => setForm((f) => ({ ...f, discount_percent: Number(e.target.value) }))}
            required
          />
          <AdminInput
            type="number"
            min={1}
            placeholder="Max uses (optional)"
            value={form.max_uses}
            onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
          />
          <AdminInput type="datetime-local" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))} />
          <AdminInput type="datetime-local" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} />
          <AdminInput placeholder="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 dark:text-slate-300">Status</span>
            <AdminSelect value={form.is_active ? 'active' : 'inactive'} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === 'active' }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </AdminSelect>
          </div>
          <div className="md:col-span-2 md:text-right">
            <AdminButton type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create promo code'}
            </AdminButton>
          </div>
        </form>
      </AdminCard>

      <AdminCard className="p-0">
        {loading ? (
          <div className="p-4 text-slate-500">Loading...</div>
        ) : (
          <AdminTable
            columns={[
              { key: 'code', label: 'Code' },
              { key: 'discount_percent', label: 'Discount', render: (row) => `${row.discount_percent}%` },
              { key: 'used_count', label: 'Used / Max', render: (row) => `${row.used_count} / ${row.max_uses ?? '∞'}` },
              { key: 'status', label: 'Status', render: (row) => <AdminStatusPill value={row.is_active ? 'active' : 'inactive'} /> },
              { key: 'expires_at', label: 'Expires', render: (row) => (row.expires_at ? new Date(row.expires_at).toLocaleString() : '-') },
              {
                key: 'actions',
                label: 'Actions',
                render: (row) => (
                  <div className="flex flex-wrap gap-2">
                    <AdminButton variant="secondary" className="px-2.5 py-1.5 text-xs" onClick={() => toggleActive(row)}>
                      {row.is_active ? 'Disable' : 'Enable'}
                    </AdminButton>
                    <AdminButton variant="danger" className="px-2.5 py-1.5 text-xs" onClick={() => deletePromo(row)}>
                      Delete
                    </AdminButton>
                  </div>
                )
              }
            ]}
            rows={items}
          />
        )}
      </AdminCard>
    </AdminPageShell>
  );
};

export default PromoCodesPage;
